/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import type { Sale } from '@/services/requests/generated/types.gen';
import type { SalePaperworkMilestoneType } from '../types/sale.types';

/**
 * Three states, deliberately. Escritura, ITBI and the registry transfer are
 * recorded as dates, not states: they land out of order and weeks apart, and
 * none is a decision the system makes. Fewer states is also less to explain
 * to someone who does not think in state machines.
 */
export type SaleState = NonNullable<Sale['state']>;

export const SALE_STATES: readonly SaleState[] = [
  'in_paperwork', 'completed', 'voided',
] as const;

export type SaleAction = 'conclude' | 'cancel';

/**
 * Which actions the UI may offer from each state. Both ends are terminal:
 * undoing a concluded sale is a new business act, not a rewind.
 */
const ACTIONS_BY_STATE: Record<SaleState, SaleAction[]> = {
  in_paperwork: ['conclude', 'cancel'],
  completed: [],
  voided: [],
};

/**
 * Lists the actions available for a sale in the given state.
 *
 * @param state - The sale's current state.
 * @returns The allowed actions, or an empty array when none apply.
 */
export function actionsFor(state: SaleState): SaleAction[] {
  return ACTIONS_BY_STATE[state] ?? [];
}

export const TARGET_STATE_BY_ACTION: Record<SaleAction, SaleState> = {
  conclude: 'completed',
  cancel: 'voided',
};

/**
 * Resolve the display label for an agency-configured milestone type.
 *
 * The four defaults (`itbi`, `deed`, `registry`, `financing`) carry stable
 * ids that match i18n keys already under `detail.milestones.*`, so they stay
 * translated in every locale even though the type itself now lives in
 * agency-configured storage. A custom type the agency added has no
 * translation key to fall back to — its own stored `label` is shown as-is,
 * the same idiom already used for the free-text "Outros" document category.
 *
 * @param type - The milestone type to label.
 * @param t - The namespace translator.
 * @returns The label to display.
 */
export function milestoneLabel(
  type: SalePaperworkMilestoneType,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return t(`detail.milestones.${type.id}`, { defaultValue: type.label });
}

/**
 * The seven ids the backend seeds every agency with. i18n translates these
 * by id (`documents.categories.<id>`); a category the agency added has no
 * key and shows its own stored label instead.
 */
export const BUILT_IN_DOCUMENT_CATEGORY_IDS = new Set([
  'deed', 'itbi', 'registry', 'commission_receipt',
  'contract', 'cancellation_deed', 'other',
]);

export const SALE_DOCUMENT_CATEGORIES = [
  'deed',
  'itbi',
  'registry',
  'commission_receipt',
  'contract',
  'cancellation_deed',
  'other',
] as const;

/**
 * The accent each state is drawn in, as a token rather than a literal, so the
 * screen follows the palette instead of carrying its own copy of it.
 *
 * Mostarda for work in progress, salvia for a sale that completed, cobre for
 * one that was undone. Cobre rather than a neutral grey on purpose: a sale
 * being undone is an outcome the reader should notice, not a muted absence.
 */
export const SALE_STATE_ACCENTS: Record<SaleState, string> = {
  in_paperwork: 'var(--accent-mostarda)',
  completed: 'var(--accent-salvia)',
  voided: 'var(--accent-cobre)',
};

/** Badge colors, using the same accent tokens as the lease badges. */
export const SALE_STATE_STYLES: Record<SaleState, { bg: string; color: string }> = {
  in_paperwork: {
    bg: 'color-mix(in srgb, var(--accent-mostarda) 16%, var(--surface-primary))',
    color: 'var(--accent-mostarda)',
  },
  completed: {
    bg: 'color-mix(in srgb, var(--accent-salvia) 16%, var(--surface-primary))',
    color: 'var(--accent-salvia)',
  },
  voided: {
    bg: 'color-mix(in srgb, var(--accent-cobre) 16%, var(--surface-primary))',
    color: 'var(--accent-cobre)',
  },
};

/**
 * Whether a sale has reached one of its two final states.
 *
 * @param state - The sale state to test.
 * @returns True for a completed or voided sale.
 */
export function isTerminal(state: string): boolean {
  return state === 'completed' || state === 'voided';
}

/**
 * Whether the sale is in the one state that can still be acted on.
 *
 * Deliberately a positive test for `in_paperwork` rather than "not
 * terminal". A record carrying some other value — a legacy state left by an
 * earlier migration, say — has no declared outgoing edge, so the backend's
 * `validate_transition` refuses every target for it. Offering the buttons
 * anyway would be offering an action that cannot succeed, so anything that is
 * not `in_paperwork` is treated as read-only.
 */
export function isOpen(state: string): boolean {
  return state === 'in_paperwork';
}

/**
 * Whether commission receipts may still be recorded.
 *
 * Wider than {@link isOpen} on purpose: commission often arrives after the
 * deal has already closed, so a concluded sale keeps taking receipts. A
 * voided sale does not — it never happened, so there is nothing to record
 * money against.
 */
export function canRecordCommission(state: string): boolean {
  return state === 'in_paperwork' || state === 'completed';
}

/**
 * The next paperwork milestone still missing, taken from the backend's own
 * `paperwork_pending` list rather than recomputed from the dates.
 *
 * The backend derives that list on read, already in the agency's configured
 * order; deriving it a second time here would be a second implementation to
 * keep in agreement, and the two would eventually disagree.
 *
 * @param pending - The `paperwork_pending` ids the backend reported.
 * @param types - The agency's configured milestone types, to resolve a label from.
 * @returns The first milestone type still pending, or null when none is.
 */
export function nextPendingMilestone(
  pending: readonly string[] | undefined,
  types: readonly SalePaperworkMilestoneType[],
): SalePaperworkMilestoneType | null {
  if (!pending?.length) return null;
  const id = pending[0];
  return types.find((m) => m.id === id) ?? null;
}

/**
 * Resolve the display label for a sale state.
 *
 * @returns A translator from state value to localized label.
 */
export function useSaleStateLabel(): (state: string) => string {
  const { t } = useTranslation('features/sales');
  return (state) => t(`states.${state}`, { defaultValue: state });
}

/**
 * Resolve the label for an action — phrased as the verb the broker performs,
 * never as the target state.
 *
 * @returns A translator from action value to localized label.
 */
export function useSaleActionLabel(): (action: string) => string {
  const { t } = useTranslation('features/sales');
  return (action) => t(`actions.${action}`, { defaultValue: action });
}
